import { useState, useEffect, useCallback } from "react";
import { MMKV } from "react-native-mmkv";

import { useIsMountedRef } from "@src/hooks/useIsMountedRef";

const mmkv = new MMKV();

export const AppStorageKeys = {
  USER_PREFERENCES: "user_preferences",
  THEME: "theme",
  ONBOARDING_COMPLETED: "onboarding_completed",
  LAST_SYNC_TIME: "last_sync_time",
  APP_VERSION: "app_version",
  FEATURE_FLAGS: "feature_flags",
  FEATURE_FLAGS_OVERRIDES: "feature_flags_overrides",
  AUTH_USER: "auth_user",
  BIOMETRIC_PREFERENCE: "biometricPreference",
  PUSH_NOTIFICATIONS_DEVICE_TOKEN: "push_notifications_device_token",
  AVAILABLE_TENANTS: "available_tenants",
  SELECTED_TENANT: "selected_tenant",
  IMPERSONATION: "impersonation",
} as const;

export type AppStorageKey = (typeof AppStorageKeys)[keyof typeof AppStorageKeys];

export class StorageError extends Error {
  constructor(
    message: string,
    public readonly operation: "get" | "set" | "remove" | "clear",
    public readonly key?: string,
  ) {
    super(message);
    this.name = "StorageError";
  }
}

interface StorageOptions<T> {
  defaultValue?: T;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
}

export function get<T = string>(key: AppStorageKey, options?: StorageOptions<T>): T | null {
  try {
    let rawValue: string | null = null;

    rawValue = mmkv.getString(key) ?? null;

    if (rawValue === null) {
      return options?.defaultValue ?? null;
    }

    if (options?.deserialize) {
      return options.deserialize(rawValue);
    }

    try {
      return JSON.parse(rawValue) as T;
    } catch {
      return rawValue as T;
    }
  } catch (error) {
    console.error(`StorageError (get): Failed to read key "${key}"`, error);
    throw new StorageError(`Failed to read key "${key}": ${error}`, "get", key);
  }
}

export function set<T>(key: AppStorageKey, value: T, options?: StorageOptions<T>): void {
  try {
    const serializedValue = options?.serialize ? options.serialize(value) : JSON.stringify(value);

    mmkv.set(key, serializedValue);
  } catch (error) {
    console.error(`StorageError (set): Failed to write key "${key}"`, error);
    throw new StorageError(`Failed to write key "${key}": ${error}`, "set", key);
  }
}

export function remove(key: AppStorageKey): void {
  try {
    mmkv.delete(key);
  } catch (error) {
    console.error(`StorageError (remove): Failed to delete key "${key}"`, error);
    throw new StorageError(`Failed to delete key "${key}": ${error}`, "remove", key);
  }
}

export function clear(): void {
  try {
    mmkv.clearAll();
  } catch (error) {
    console.error("StorageError (clear): Failed to clear storage", error);
    throw new StorageError(`Failed to clear storage: ${error}`, "clear");
  }
}

export function has(key: AppStorageKey): boolean {
  try {
    return mmkv.contains(key);
  } catch (error) {
    console.error(`StorageError (has): Failed to check key "${key}"`, error);
    return false;
  }
}

export function getAllKeys(): string[] {
  try {
    return mmkv.getAllKeys();
  } catch (error) {
    console.error("StorageError (getAllKeys): Failed to get all keys", error);
    return [];
  }
}

type StorageListener<T> = (value: T | null) => void;
const listeners = new Map<string, Set<StorageListener<unknown>>>();

function addListener<T>(key: AppStorageKey, listener: StorageListener<T>): () => void {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key)!.add(listener as StorageListener<unknown>);

  return () => {
    const keyListeners = listeners.get(key);
    if (keyListeners) {
      keyListeners.delete(listener as StorageListener<unknown>);
      if (keyListeners.size === 0) {
        listeners.delete(key);
      }
    }
  };
}

function notifyListeners<T>(key: AppStorageKey, value: T | null): void {
  const keyListeners = listeners.get(key);
  if (keyListeners) {
    keyListeners.forEach((listener) => {
      (listener as StorageListener<T>)(value);
    });
  }
}

export function useStorage<T = string>(
  key: AppStorageKey,
  options?: StorageOptions<T>,
): [T | null, (value: T | null) => void, boolean, Error | null] {
  const [value, setValue] = useState<T | null>(options?.defaultValue ?? null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isMounted = useIsMountedRef();

  useEffect(() => {
    try {
      setIsLoading(true);
      const storedValue = get<T>(key, options);
      setValue(storedValue);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [key, options]);

  useEffect(() => {
    const unsubscribe = addListener<T>(key, (newValue) => {
      setValue(newValue);
    });

    return unsubscribe;
  }, [key]);

  const updateValue = useCallback(
    (newValue: T | null) => {
      try {
        if (newValue === null) {
          remove(key);
        } else {
          set(key, newValue, options);
        }
        setValue(newValue);
        notifyListeners(key, newValue);
        setError(null);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [key, options],
  );

  return [value, updateValue, isLoading, error];
}

export function useBooleanStorage(
  key: AppStorageKey,
  defaultValue = false,
): [boolean, () => void, (value: boolean) => void, boolean, Error | null] {
  const [value, setValue, isLoading, error] = useStorage<boolean>(key, {
    defaultValue,
  });

  const toggle = useCallback(() => {
    setValue(!value);
  }, [value, setValue]);

  return [value ?? defaultValue, toggle, setValue, isLoading, error];
}

export const appStorage = {
  get,
  set,
  remove,
  clear,
  has,
  getAllKeys,
};

// Generic storage for custom keys (not limited to AppStorageKeys)
export const genericStorage = {
  get<T = string>(key: string, defaultValue?: T): T | null {
    try {
      const rawValue = mmkv?.getString(key) ?? null;

      if (rawValue === null) {
        return defaultValue ?? null;
      }

      try {
        return JSON.parse(rawValue) as T;
      } catch {
        return rawValue as T;
      }
    } catch (error) {
      console.error(`StorageError (get): Failed to read key "${key}"`, error);
      return defaultValue ?? null;
    }
  },

  set<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      mmkv?.set(key, serializedValue);
    } catch (error) {
      console.error(`StorageError (set): Failed to write key "${key}"`, error);
    }
  },

  remove(key: string): void {
    try {
      mmkv?.delete(key);
    } catch (error) {
      console.error(`StorageError (remove): Failed to delete key "${key}"`, error);
    }
  },

  has(key: string): boolean {
    try {
      return mmkv?.contains(key) ?? false;
    } catch (error) {
      console.error(`StorageError (has): Failed to check key "${key}"`, error);
      return false;
    }
  },
};

// Biometric Preferences
// biometricEnabled: undefined = user hasn't made a choice yet
// biometricEnabled: true = user chose to enable biometrics
// biometricEnabled: false = user chose to use passcode only
export type BiometricPreference = {
  biometricEnabled: boolean | undefined;
};
